export type THeadcountCategoryLevel = 'level_1' | 'level_2' | 'functions';

export type THeadcountEmploymentType = 'fte' | 'temporary';

export const HEADCOUNT_CATEGORY_LEVEL_OPTIONS: {
  value: THeadcountCategoryLevel;
  labelKey: string;
  tipKey: string;
}[] = [
    {
      value: 'level_1',
      labelKey: 'dbs_studios_headcount_level_1',
      tipKey: 'dbs_studios_headcount_level_1_tip',
    },
    {
      value: 'level_2',
      labelKey: 'dbs_studios_headcount_level_2',
      tipKey: 'dbs_studios_headcount_level_2_tip',
    },
    {
      value: 'functions',
      labelKey: 'dbs_studios_headcount_functions',
      tipKey: 'dbs_studios_headcount_functions_tip',
    },
  ];

export const FTE_LEAF_KEYS = [
  'management',
  'technology_and_research',
  'product_management_and_business_operations_and_development',
  'design',
  'sales_and_marketing',
  'functional_support',
  'customer_service',
] as const;

export const TEMPORARY_LEAF_KEYS = [
  'intern',
  'contractor',
  'others',
] as const;

export type THeadcountLeafKey =
  | typeof FTE_LEAF_KEYS[number]
  | typeof TEMPORARY_LEAF_KEYS[number];

export interface IHeadcountBarGradient {
  top: string;
  bottom: string;
}

export interface IHeadcountChartSeriesConfig {
  chartKey: string;
  employmentType: THeadcountEmploymentType;
  leafKeys: readonly string[];
  labelKey: string;
  // Solid color for legend markers and tooltip dots.
  color: string;
  // Level 1 bars use a vertical linear gradient per Figma.
  barGradient?: IHeadcountBarGradient;
}

// Colors are bound directly to each series' chartKey (per Figma), not assigned
// by array position — this keeps a given category the same color across
// Level 1/2/Functions (e.g. Intern is always #B5EFFF) regardless of how many
// sibling series happen to be visible at that level.
// See: https://www.figma.com/design/WCReREn2S1axAxRgBHDgwz/?node-id=35436-39363 (Functions)
//      https://www.figma.com/design/WCReREn2S1axAxRgBHDgwz/?node-id=35140-32539 (Level 2)
const HEADCOUNT_MARKER_COLORS: Record<string, string> = {
  // Level 1 — solid marker for legend/tooltip (bar fill uses barGradient)
  fte: '#7F6FF1',
  temporary: '#56BCE6',
  // Shared across Level 2 and Functions
  management: '#D2C8FD',
  functional_support: '#1B1C59',
  intern: '#B5EFFF',
  contractor: '#5EC8F1',
  others: '#307DA6',
  // Level 2 only (aggregated business-area buckets)
  r_and_d: '#7F6FF1',
  s_and_m: '#413CA6',
  liveops: '#292ba3',
  // Functions only (granular leaves)
  technology_and_research: '#AD9EFF',
  product_management_and_business_operations_and_development: '#7F6FF1',
  design: '#5E54CC',
  sales_and_marketing: '#413CA6',
  customer_service: '#292880',
};

const DEFAULT_MARKER_COLOR = '#7F6FF1';

// Level 1 category bars: vertical linear gradient (top → bottom).
const LEVEL_1_BAR_GRADIENTS: Record<string, IHeadcountBarGradient> = {
  fte: { top: '#AD9EFF', bottom: '#685BC2' },
  temporary: { top: '#8CE2FF', bottom: '#56BCE6' },
};

const LEVEL_1_SERIES: Omit<IHeadcountChartSeriesConfig, 'color' | 'barGradient'>[] = [
  {
    chartKey: 'fte',
    employmentType: 'fte',
    leafKeys: FTE_LEAF_KEYS,
    labelKey: 'dbs_studios_headcount_fte',
  },
  {
    chartKey: 'temporary',
    employmentType: 'temporary',
    leafKeys: TEMPORARY_LEAF_KEYS,
    labelKey: 'dbs_studios_headcount_temporary',
  },
];
const LEVEL_1_SERIES_YEAR: Omit<IHeadcountChartSeriesConfig, 'color' | 'barGradient'>[] = [
  {
    chartKey: 'fte',
    employmentType: 'fte',
    leafKeys: ['fte'],
    labelKey: 'dbs_studios_headcount_fte',
  },
  {
    chartKey: 'temporary',
    employmentType: 'temporary',
    leafKeys: ['temporary'],
    labelKey: 'dbs_studios_headcount_temporary',
  },
];

const LEVEL_2_SERIES: Omit<IHeadcountChartSeriesConfig, 'color' | 'barGradient'>[] = [
  {
    chartKey: 'management',
    employmentType: 'fte',
    leafKeys: ['management'],
    labelKey: 'dbs_studios_headcount_management',
  },
  {
    chartKey: 'r_and_d',
    employmentType: 'fte',
    leafKeys: [
      'technology_and_research',
      'product_management_and_business_operations_and_development',
      'design',
    ],
    labelKey: 'dbs_rd',
  },
  {
    chartKey: 's_and_m',
    employmentType: 'fte',
    leafKeys: ['sales_and_marketing', 'customer_service'],
    labelKey: 'dbs_sm',
  },
  {
    chartKey: 'functional_support',
    employmentType: 'fte',
    leafKeys: ['functional_support'],
    labelKey: 'dbs_studios_headcount_functional_support',
  },
  {
    chartKey: 'intern',
    employmentType: 'temporary',
    leafKeys: ['intern'],
    labelKey: 'dbs_studios_headcount_intern',
  },
  {
    chartKey: 'contractor',
    employmentType: 'temporary',
    leafKeys: ['contractor'],
    labelKey: 'dbs_studios_headcount_contractor',
  },
  {
    chartKey: 'others',
    employmentType: 'temporary',
    leafKeys: ['others'],
    labelKey: 'dbs_studios_headcount_others',
  },
];

const LEVEL_2_SERIES_FORECAST: Omit<IHeadcountChartSeriesConfig, 'color' | 'barGradient'>[] = [
  {
    chartKey: 'management',
    employmentType: 'fte',
    leafKeys: ['l2_management'],
    labelKey: 'dbs_studios_headcount_management',
  },
  {
    chartKey: 'r_and_d',
    employmentType: 'fte',
    leafKeys: ['l2_r_and_d'],
    labelKey: 'dbs_rd',
  },
  {
    chartKey: 's_and_m',
    employmentType: 'fte',
    leafKeys: ['l2_s_and_m'],
    labelKey: 'dbs_sm',
  },
  {
    chartKey: 'functional_support',
    employmentType: 'fte',
    leafKeys: ['l2_functional_support'],
    labelKey: 'dbs_studios_headcount_functional_support',
  },
  {
    chartKey: 'intern',
    employmentType: 'temporary',
    leafKeys: ['intern'],
    labelKey: 'dbs_studios_headcount_intern',
  },
  {
    chartKey: 'contractor',
    employmentType: 'temporary',
    leafKeys: ['contractor'],
    labelKey: 'dbs_studios_headcount_contractor',
  },
  {
    chartKey: 'others',
    employmentType: 'temporary',
    leafKeys: ['others'],
    labelKey: 'dbs_studios_headcount_others',
  },
];

const FUNCTIONS_SERIES: Omit<IHeadcountChartSeriesConfig, 'color' | 'barGradient'>[] = [
  {
    chartKey: 'management',
    employmentType: 'fte',
    leafKeys: ['management'],
    labelKey: 'dbs_studios_headcount_management',
  },
  {
    chartKey: 'technology_and_research',
    employmentType: 'fte',
    leafKeys: ['technology_and_research'],
    labelKey: 'dbs_studios_headcount_technology_and_research',
  },
  {
    chartKey: 'product_management_and_business_operations_and_development',
    employmentType: 'fte',
    leafKeys: ['product_management_and_business_operations_and_development'],
    labelKey: 'dbs_studios_headcount_product_management',
  },
  {
    chartKey: 'design',
    employmentType: 'fte',
    leafKeys: ['design'],
    labelKey: 'dbs_studios_headcount_design',
  },
  {
    chartKey: 'sales_and_marketing',
    employmentType: 'fte',
    leafKeys: ['sales_and_marketing'],
    labelKey: 'dbs_studios_headcount_sales_and_marketing',
  },
  {
    chartKey: 'customer_service',
    employmentType: 'fte',
    leafKeys: ['customer_service'],
    labelKey: 'dbs_studios_headcount_customer_service',
  },
  {
    chartKey: 'functional_support',
    employmentType: 'fte',
    leafKeys: ['functional_support'],
    labelKey: 'dbs_studios_headcount_functional_support',
  },
  {
    chartKey: 'intern',
    employmentType: 'temporary',
    leafKeys: ['intern'],
    labelKey: 'dbs_studios_headcount_intern',
  },
  {
    chartKey: 'contractor',
    employmentType: 'temporary',
    leafKeys: ['contractor'],
    labelKey: 'dbs_studios_headcount_contractor',
  },
  {
    chartKey: 'others',
    employmentType: 'temporary',
    leafKeys: ['others'],
    labelKey: 'dbs_studios_headcount_others',
  },
];

const assignSeriesColors = (
  series: Omit<IHeadcountChartSeriesConfig, 'color' | 'barGradient'>[],
  level?: THeadcountCategoryLevel,
): IHeadcountChartSeriesConfig[] => (
  series.map((item) => ({
    ...item,
    color: HEADCOUNT_MARKER_COLORS[item.chartKey] || DEFAULT_MARKER_COLOR,
    ...(level === 'level_1' && LEVEL_1_BAR_GRADIENTS[item.chartKey]
      ? { barGradient: LEVEL_1_BAR_GRADIENTS[item.chartKey] }
      : {}),
  }))
);

// 数据集中任一行存在 l2_liveops 字段时，Level 2 需要展示 LiveOps 系列
export const hasHeadcountLiveopsData = (
  rawRows: Record<string, unknown>[],
): boolean => rawRows.some(
  (row: any) => row?.l2_liveops !== null && row?.l2_liveops !== undefined,
);

export const getHeadcountChartSeriesConfig = (
  level: THeadcountCategoryLevel,
  monthRow?: any,
  activeDateTypeSegment?: string,
  hasLiveops?: boolean,
): IHeadcountChartSeriesConfig[] => {
  if (level === 'level_1') {
    if (activeDateTypeSegment === 'year') {
      return assignSeriesColors(LEVEL_1_SERIES_YEAR, 'level_1');
    }
    return assignSeriesColors(LEVEL_1_SERIES, 'level_1');
  }
  if (level === 'level_2') {
    let seriesTemp = LEVEL_2_SERIES;

    if (monthRow?.source === 'forecast') {
      seriesTemp = LEVEL_2_SERIES_FORECAST;
    }

    // 优先使用数据集级 hasLiveops；未传时回退到单行 l2_liveops 判断。
    // 存在 l2_liveops 数据时，在 s_and_m 系列后插入 liveops 系列
    const showLiveops = hasLiveops
      ?? (monthRow?.l2_liveops !== null && monthRow?.l2_liveops !== undefined);
    if (showLiveops) {
      const liveopsSeries = {
        chartKey: 'liveops',
        employmentType: 'fte' as const,
        leafKeys: ['l2_liveops'],
        labelKey: 'dbs_le_liveops',
      };
      const sAndMIndex = seriesTemp.findIndex((series) => series.chartKey === 's_and_m');
      if (sAndMIndex !== -1) {
        // 拷贝后再插入，避免直接修改 LEVEL_2_SERIES / LEVEL_2_SERIES_FORECAST 常量
        const seriesWithLiveops = [...seriesTemp];
        seriesWithLiveops.splice(sAndMIndex + 1, 0, liveopsSeries);
        seriesTemp = seriesWithLiveops;
      }
    }

    return assignSeriesColors(seriesTemp);
  }
  return assignSeriesColors(FUNCTIONS_SERIES);
};

// ECharts stacks series in array order (first = bottom). Reverse within each
// employment stack so the first config row (e.g. Management) renders on top.
export const getHeadcountStackSeriesConfig = (
  level: THeadcountCategoryLevel,
  hasLiveops?: boolean,
): IHeadcountChartSeriesConfig[] => {
  const seriesConfig = getHeadcountChartSeriesConfig(
    level,
    undefined,
    undefined,
    hasLiveops,
  );
  const fteSeries = seriesConfig
    .filter((series) => series.employmentType === 'fte')
    .reverse();
  const temporarySeries = seriesConfig
    .filter((series) => series.employmentType === 'temporary')
    .reverse();

  return [...fteSeries, ...temporarySeries];
};

export const HEADCOUNT_CHART_VALUE_TYPE = 'integer';

export const buildHeadcountTooltipSeries = (
  level: THeadcountCategoryLevel,
  formatLabel: (labelKey: string) => string,
  hasLiveops?: boolean,
) => getHeadcountChartSeriesConfig(
  level,
  undefined,
  undefined,
  hasLiveops,
).map((series) => ({
  groupKey: series.employmentType,
  rowKey: series.chartKey,
  label: formatLabel(series.labelKey),
  markerColor: series.color,
}));

export const sumHeadcountLeafValues = (
  row: Record<string, unknown>,
  leafKeys: readonly string[],
  level: THeadcountCategoryLevel,
  chartKey: string,
): any | null => {

  let sum = 0;
  let hasValue = false;

  leafKeys.forEach((key) => {
    const rawValue = row[key];
    if (rawValue === null || rawValue === undefined) {
      return;
    }
    const numericValue = Number(rawValue);
    if (!Number.isFinite(numericValue)) {
      return;
    }
    sum += numericValue;
    hasValue = true;
  });

  // 如果是level_1,并且source:为"forecast"时，fte和temporary直接取本身
  if (level === 'level_1' && row.source === 'forecast') {
    return row?.[chartKey] ? row?.[chartKey] : null;
  }

  return hasValue ? sum : null;
};

export const getHeadcountEmploymentStackName = (
  employmentType: THeadcountEmploymentType,
): string => (employmentType === 'fte' ? 'FTE' : 'Temporary');

export const buildHeadcountChartTableRows = (
  rawRows: Record<string, unknown>[],
  level: THeadcountCategoryLevel,
  activeDateTypeSegment?: string,
): Record<string, unknown>[] => {

  // 数据集级判断，保证每行 chartKey 一致（含 liveops），避免图表数据错位
  const hasLiveops = hasHeadcountLiveopsData(rawRows);

  return rawRows.map((monthRow: any) => {
    const seriesConfig = getHeadcountChartSeriesConfig(
      level,
      monthRow,
      activeDateTypeSegment,
      hasLiveops,
    );
    const chartRow: Record<string, unknown> = {
      // Preserve normalized axis fields for Studios (date) and Portfolio (studio label).
      key: monthRow.key ?? monthRow.date,
      label: monthRow.label ?? monthRow.date,
      // Backend unified on `date` for both by_month (YYYY-MM) and by_year (YYYY).
      date: monthRow.date,
      // Optional tooltip title (Portfolio maps studio_name → title).
      title: monthRow.title ?? monthRow.studio_name,
      // actual | forecast — used to dim forecast bars
      source: monthRow.source,
    };
    seriesConfig.forEach((series) => {
      chartRow[series.chartKey] = sumHeadcountLeafValues(monthRow, series.leafKeys, level, series?.chartKey);
    });

    return chartRow;
  });
};
